import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import React, { type FormEvent, useState } from 'react';
import { Flexor } from 'shared/src/components';
import DeprecatedModal from 'shared/src/components/DeprecatedModal';
import EasyVoteLogo from 'shared/src/components/EasyVoteLogo';
import Spinner from 'shared/src/components/Spinner';
import { Alert, AlertDescription, AlertTitle, Button, Input, Label } from 'shared/src/components/ui/new';
import { cn } from 'shared/src/utils';

type LoginProps = {
  logo?: string;
  errorMessage?: string;
  onForgotPassword?: () => void;
  login: (username: string, password: string) => void;
  isLoggingIn: boolean;
  className?: string;
  framed?: boolean;
};

export default function Login({ login, errorMessage, onForgotPassword, logo, isLoggingIn, className, framed = false }: LoginProps) {
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [showForgotAlert, setShowForgotAlert] = useState<boolean>(false);

  function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!username || !password) return;

    login(username, password);
  }

  return (
    <>
      <DeprecatedModal
        onClose={() => setShowForgotAlert(false)}
        title="Notice"
        icon={<ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />}
        iconColor="bg-yellow-100"
        open={showForgotAlert}
      >
        If you forgot your password, please use the EasyVote desktop app to reset it.
      </DeprecatedModal>
      <div className={cn('theme-ev flex min-h-full flex-col justify-center py-12 xs:px-6 lg:px-8', className)}>
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <EasyVoteLogo className="mx-auto h-14 w-auto" />
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className={cn(framed && 'bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10')}>
            {errorMessage ? (
              <Alert className="mb-2" variant="destructive">
                <ExclamationCircleIcon className="size-5" />
                <AlertTitle>We weren't able to log you in</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            ) : null}
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="email">Username</Label>
                <div className="mt-2">
                  <Input id="email" name="email" type="text" autoComplete="email" required onChange={({ target: { value } }) => setUsername(value)} />
                </div>
              </div>

              <div>
                <Flexor>
                  <Label htmlFor="password">Password</Label>
                  <Button
                    type="button"
                    variant="link"
                    className="text-sm !p-0 !h-auto"
                    onClick={() => {
                      if (onForgotPassword) {
                        onForgotPassword();
                        return;
                      }

                      setShowForgotAlert(true);
                    }}
                    tabIndex={-1}
                  >
                    Forgot?
                  </Button>
                </Flexor>
                <div className="mt-2">
                  <Input id="password" name="password" type="password" onChange={({ target: { value } }) => setPassword(value)} autoComplete="current-password" required />
                </div>
              </div>

              <Button data-testid="login-submit" disabled={isLoggingIn} type="submit" className="w-full flex items-center space-x-2">
                <Spinner show={isLoggingIn} light />
                <span>Sign in</span>
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
